import { CommonErrorType } from '../../common/enums.js';
import {
  closeMsg,
  companyCreditLimitExceededEmployeeMsg,
  creditDecisionFailureMsg,
  creditDecisionFailureRecommendCardPaymentMsg,
  employeeCreditCheckFailedMsg,
  processingOrderMsg,
  t,
} from '../../common/i18n/index.js';
import { AuthenticatedUserRole, CreditCheckDecision } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { LoadingDialog } from '../LoadingDialog/LoadingDialog.js';
import type { DialogData } from '../../public/site/dialogStore.js';

const ELISA_CONTACT_TERM_LINK = 'https://yrityksille.elisa.fi/ohje/elisan-sopimusehdot-yritysasiakkaille';

const isCreditCheckFailure = (errorMessage: string) =>
  errorMessage === CreditCheckDecision.COMPANY_CREDIT_LIMIT_EXCEEDED ||
  errorMessage === CreditCheckDecision.COMPANY_NEGATIVE_CREDIT_DECISION ||
  errorMessage === CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION ||
  errorMessage === CreditCheckDecision.DECLINED ||
  errorMessage === CreditCheckDecision.DECLINED_RECOMMEND_CARD_PAYMENT;

type CreditCheckError = { message: string; pdfLink?: string };

const getAdminCreditCheckErrorCct = (creditCheckDecision: string): CreditCheckError => {
  return creditCheckDecision === CreditCheckDecision.DECLINED_RECOMMEND_CARD_PAYMENT
    ? {
        message: t.R8HE(creditDecisionFailureRecommendCardPaymentMsg),
        pdfLink: ELISA_CONTACT_TERM_LINK,
      }
    : {
        message: t.WXZC(creditDecisionFailureMsg),
        pdfLink: ELISA_CONTACT_TERM_LINK,
      };
};

const getEmployeeCreditCheckErrorCct = (creditCheckDecision: string): CreditCheckError => {
  return creditCheckDecision === CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION
    ? {
        message: t.ZE11(employeeCreditCheckFailedMsg),
        pdfLink: t.KA37('https://elisa.fi/attachment/content/Elisa_Yleiset_sopimusehdot_ENG.pdf'),
      }
    : { message: t.GCDV(companyCreditLimitExceededEmployeeMsg) };
};

const getCreditCheckError = ({
  creditCheckFailure,
  userRole,
}: {
  creditCheckFailure: string;
  userRole?: AuthenticatedUserRole;
}): CreditCheckError => {
  const isAdminOrKeyUser = userRole === AuthenticatedUserRole.ADMIN || userRole === AuthenticatedUserRole.KEY_USER;

  return isAdminOrKeyUser
    ? getAdminCreditCheckErrorCct(creditCheckFailure)
    : getEmployeeCreditCheckErrorCct(creditCheckFailure);
};

const CreditCheckDialog = ({
  creditCheckFailure,
  redirectPath,
  onCloseDialog,
}: {
  creditCheckFailure: string;
  redirectPath: string;
  onCloseDialog: () => void;
}) => {
  const { authenticatedUser } = useAuth();
  const { message, pdfLink } = getCreditCheckError({
    creditCheckFailure,
    userRole: authenticatedUser?.userRole,
  });

  const isClosable =
    creditCheckFailure === CreditCheckDecision.DECLINED_RECOMMEND_CARD_PAYMENT &&
    authenticatedUser?.userRole !== AuthenticatedUserRole.EMPLOYEE;

  return (
    <DialogWrapper
      buttons={
        isClosable
          ? [
              {
                text: t.WOYD(closeMsg),
                onClick: onCloseDialog,
              },
            ]
          : undefined
      }
      closeable={isClosable}
      header={t.ZK30('Finalizing the order failed')}
      links={
        !isClosable
          ? [
              {
                href: redirectPath,
                text: t.PLAW('Return to the shop'),
              },
            ]
          : undefined
      }
      onCloseDialog={onCloseDialog}
    >
      <p>
        {message}
        {pdfLink && (
          <>
            {' '}
            <a href={pdfLink} target="_blank" rel="noreferrer">
              {t.Z3L0('More information')}
            </a>
          </>
        )}
      </p>
    </DialogWrapper>
  );
};

export type SubmitOrderDialogProps = {
  dialog?: DialogData;
  redirectPath: string;
  onClickHome: () => void;
  onCloseDialog: () => void;
};

export const SubmitOrderDialog = (props: SubmitOrderDialogProps) => {
  if (props.dialog?.errors && props.dialog.errors.length > 0) {
    const dialogError = props.dialog.errors[0];

    if (isCreditCheckFailure(dialogError.message)) {
      return (
        <CreditCheckDialog
          creditCheckFailure={dialogError.message}
          redirectPath={props.redirectPath}
          onCloseDialog={props.onCloseDialog}
        />
      );
    }

    const loadingDialogError = {
      buttonAction: props.onClickHome,
      buttonText: t.PLAW('Return to the shop'),
      header:
        dialogError.type === CommonErrorType.SYSTEM ? t.V0VJ('Oops, something went wrong') : t.N3V5('What a shame'),
      message: dialogError.message,
    };

    return <LoadingDialog error={loadingDialogError} loadingText={t.RW6O(processingOrderMsg)} />;
  }
  return <LoadingDialog loadingText={t.RW6O(processingOrderMsg)} />;
};
